import type { IInterestListFiltersProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { useSite } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { FilterTeams } from '@public/components/Db/Filters/FilterTeams';
import { type ILinkFilterItem } from '@public/components/Db/Filters/LinkFilter';
import { useSortedConferences } from '@public/utils/hooks/useSortedConferences';
import clsx from 'clsx';

import styles from './InterestListFilters.module.scss';

const interestRoutes: ILinkFilterItem[] = [
  {
    key: 1,
    name: 'Commits',
    pathname: '/college/[college]/[sport]/[year]/industry-comparison-commits',
  },
  {
    key: 2,
    name: 'Decommits',
    pathname: '/college/[college]/[sport]/[year]/industry-comparison-decommits',
  },
  {
    key: 3,
    name: 'Visits',
    pathname: '/college/[college]/[sport]/[year]/visits',
  },
  {
    key: 4,
    name: 'Offers',
    pathname: '/college/[college]/[sport]/[year]/offers',
  },
];

const commitDecommitRoutes: ILinkFilterItem[] = [
  {
    key: 1,
    name: 'Commits',
    pathname: '/recruiting/latest-commits',
  },
  {
    key: 2,
    name: 'Decommits',
    pathname: '/recruiting/latest-decommits',
  },
];
const allObject = { label: 'All', value: '' };

export const InterestListFilters = ({
  years,
  teams,
  className,
}: IInterestListFiltersProps) => {
  const { rankingSports } = useSite();

  const sortedConferences = useSortedConferences(teams ?? []);
  const { formatHref, router } = useDynamicRoute();
  const isLatestCommitsPage = router.asPath.includes('latest-commits');

  const isLatestDecommitsPage = router.asPath.includes('latest-decommits');
  const isLatest = isLatestCommitsPage || isLatestDecommitsPage;
  const { year = 'All', sport = 'All' } = router.query as {
    year: string;
    sport: string;
  };

  const displayStatusRoute =
    isLatestCommitsPage || isLatestDecommitsPage
      ? commitDecommitRoutes
      : interestRoutes;

  return (
    <div className={clsx(styles.filterWrapper, className)}>
      {teams && <FilterTeams name="college" teams={sortedConferences} />}
      <Filter
        name="year"
        navigate={(_, value) =>
          formatHref(
            isLatest
              ? {
                  query: {
                    year: value === '' ? undefined : value,
                    page: undefined,
                  },
                }
              : { params: { year: value } },
          )
        }
        options={[
          ...(isLatest ? [allObject] : []),
          ...(years || []).map((y) => ({
            label: String(y) || '',
            value: String(y) || '',
          })),
        ]}
        value={String(year)}
      />
      <Filter
        name="sport"
        navigate={(_, value) =>
          formatHref(
            isLatest
              ? {
                  query: {
                    sport: value === '' ? undefined : value,
                    page: undefined,
                  },
                }
              : { params: { sport: value } },
          )
        }
        options={[
          ...(isLatest ? [allObject] : []),
          ...(rankingSports || []).map((s) => ({
            label: s.name || '',
            value: slugify(s.name) || '',
          })),
        ]}
        value={sport}
      />

      <Filter
        name="interest-type"
        navigate={(_, v) =>
          formatHref({
            path: v,
            query: { page: undefined },
          })
        }
        options={[
          ...displayStatusRoute.map((r) => ({
            label: r.name,
            value: r.pathname,
          })),
        ]}
        value={router.pathname}
      />
    </div>
  );
};
